import { useCallback, useEffect, useRef, useState } from 'react';
import {
  canjear,
  cierreDeParte,
  completar,
  ErrorDeApi,
  guardar,
  guardarPerfil,
  leerEstado,
  leerPerfil,
  type DatosDelPerfil,
  type EstadoDelCuestionario,
  type MotivoDeFallo,
  type Pregunta,
  type TextosDelPerfil,
  type ValorDeRespuesta,
} from './api';
import { Mapa } from './Mapa';
import { Perfil } from './Perfil';
import { PreguntaEnPantalla } from './Pregunta';
import { contarProgreso, primerGrupoConAlgunaDe } from './progreso';
import { ResultadosConocidos } from './ResultadosConocidos';
import {
  duracionDelPaso,
  pantallaDePaso,
  ritmoDelPaso,
  type PantallaDePaso,
} from './retroalimentacion';
import { tituloDeGrupo } from './titulos';

/**
 * El cuestionario de diagnóstico, tal como lo ve quien lo responde.
 *
 * CÓMO SE LLEGA AQUÍ: por un enlace de WhatsApp con la forma `/d/<token>`. Lo
 * primero que hace la página es cambiar ese token por una sesión y BORRARLO DE
 * LA BARRA DE DIRECCIONES, para que no quede en el historial del navegador ni se
 * comparta sin querer al pasar la dirección a alguien.
 *
 * SE RESPONDE POR GRUPOS y no en una sola página de 75 ítems. Una lista de 75
 * preguntas en un móvil se abandona; de cinco en cinco, cada pantalla se termina.
 *
 * SE GUARDA SOLO, según se responde. Nadie contesta 75 ítems de una sentada, y
 * un botón de «enviar» al final convierte cualquier cierre del navegador en
 * media hora perdida que no se repite.
 *
 * SE DA POR HECHO QUE LA RED FALLA, porque esto se responde en un móvil y por la
 * calle. Todo lo que se escribe puede no llegar, así que se lleva la cuenta de
 * lo que no llegó, se reintenta al pasar de parte, y si aun así el servidor
 * rechaza el cierre, se vuelve a la pregunta que falta en vez de dejar a alguien
 * pulsando «Terminar» contra un error que no le dice nada.
 *
 * NI UNA PALABRA CLÍNICA EN ESTE ARCHIVO. Los enunciados, la introducción y el
 * texto de cierre vienen del contenido que la psicóloga escribe desde el panel.
 * Lo que sí se escribe aquí son los rótulos de la interfaz —«Siguiente»,
 * «Parte 3 de 15»—, que no dicen nada sobre nadie.
 */

type Estado =
  | { fase: 'cargando' }
  /**
   * Antes del cuestionario, y solo la primera vez: quién es y cómo hablarle.
   * Si la psicóloga no ha escrito esas preguntas, esta fase no existe y se
   * entra directo a `respondiendo`, como antes de que la pantalla existiera.
   */
  | { fase: 'perfil'; textos: TextosDelPerfil }
  | { fase: 'respondiendo'; datos: EstadoDelCuestionario }
  /**
   * Poniendo el resultado que ya se conoce, en vez de responder el test. Se
   * entra desde la primera parte y se sale por el mismo final que todos.
   */
  | { fase: 'resultadosConocidos'; datos: EstadoDelCuestionario }
  | { fase: 'completado'; mensaje: string | null }
  | { fase: 'fallo'; motivo: MotivoDeFallo };

/** Qué pasó al pulsar «Terminar», si es que pasó algo. */
type ProblemaAlTerminar =
  /** El servidor dice que le faltan respuestas: se volvió a la que falta. */
  | 'faltan'
  /** No se pudo hablar con el servidor. Se vuelve a pulsar y ya. */
  | 'no_salio';

/** Cuánto se espera antes de guardar lo que se está escribiendo. */
const ESPERA_DE_ESCRITURA_MS = 800;

/**
 * Cuánto dura la pantalla de cierre.
 *
 * VA EMPAREJADO CON LA ANIMACIÓN `celebrar` DE estilos.css. La animación entra,
 * se sostiene y se va sola dentro de este mismo tiempo, y el temporizador de
 * aquí es el que desmonta la capa cuando ya está en opacidad cero. Si uno de los
 * dos cambia sin el otro, o aparece un parpadeo al final o queda una capa
 * invisible tapando la pantalla.
 */
const DURACION_CELEBRACION_MS = 2900;

/** Cuánto tarda en apagarse al salir de la pantalla de paso. */
const SALIDA_FEEDBACK_MS = 450;

/**
 * Hasta cuándo se espera el cierre que se escribe en el momento.
 *
 * OCHO SEGUNDOS ES EL TOPE, NO LO QUE SE TARDA: lo normal es uno a tres. El tope
 * existe porque el peor caso no tiene techo, y quedarse mirando «Un momento…»
 * sin saber cuánto es lo que hace que la gente cierre la página. Cumplido el
 * plazo se sigue sin cierre —que es un estado normal y previsto—, y si el texto
 * llega después ya no se enseña: aparecería sobre la parte siguiente, encima de
 * unas preguntas que no tienen nada que ver con él.
 */
const TOPE_DE_ESPERA_DEL_CIERRE_MS = 8000;

/**
 * Cuánto se le da al cierre para llegar antes de abrir una pantalla a esperarlo.
 *
 * SIN ESTE UMBRAL, EL CASO MÁS FRECUENTE ES EL MÁS FEO. Cuando una parte no
 * está configurada para cerrarse con un texto escrito en el momento —que es como
 * vienen las quince de fábrica—, el servidor contesta «no hay texto» en un
 * cuarto de segundo, y abrir una pantalla completa para
 * quitarla enseguida deja un parpadeo entre cada parte. Con el umbral, esa
 * respuesta llega antes de que se abra nada y la persona pasa de parte como si
 * esto no existiera.
 *
 * Y el botón no se queda mudo mientras tanto: pasa a «Guardando…» en cuanto se
 * pulsa, que es lo que ya hacía al terminar el cuestionario.
 */
const UMBRAL_ANTES_DE_ESPERAR_MS = 400;

/** Lo que devuelve la carrera cuando el cierre no llegó a tiempo. */
const TARDA = Symbol('el cierre todavía no ha llegado');

/** Una promesa que se resuelve sola al cabo de un rato. */
function alCabo<T>(milisegundos: number, valor: T): Promise<T> {
  return new Promise((resolver) =>
    setTimeout(() => {
      resolver(valor);
    }, milisegundos),
  );
}

/** El token del enlace, si la dirección lo trae. */
function tokenDeLaDireccion(): string | null {
  const encontrado = /^\/d\/([^/?#]+)/.exec(window.location.pathname);
  return encontrado?.[1] ? decodeURIComponent(encontrado[1]) : null;
}

/** El identificador del elemento de una pregunta, para poder desplazarse a ella. */
function anclaDe(preguntaId: string): string {
  return `pregunta-${preguntaId}`;
}

export function App() {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  const [grupoActual, setGrupoActual] = useState(0);
  const [resaltarPendientes, setResaltarPendientes] = useState(false);
  const [problemaAlTerminar, setProblemaAlTerminar] = useState<ProblemaAlTerminar | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);
  /** Por qué no se pudo guardar el perfil. Se pinta bajo el formulario. */
  const [falloDelPerfil, setFalloDelPerfil] = useState<string | null>(null);
  const [celebrando, setCelebrando] = useState(false);
  const [desplazado, setDesplazado] = useState(false);

  /**
   * Lo que se lee al pasar de una parte a otra: lo que cierra la que termina y
   * lo que abre la que viene. Mientras esté puesto tapa la pantalla; al cerrarse
   * se salta a `siguiente`.
   */
  const [pasoDeGrupo, setPasoDeGrupo] = useState<(PantallaDePaso & { siguiente: number }) | null>(
    null,
  );
  const [feedbackSaliendo, setFeedbackSaliendo] = useState(false);

  /**
   * Las preguntas cuya última escritura no llegó al servidor.
   *
   * SE GUARDAN PORQUE LA PANTALLA Y EL SERVIDOR PUEDEN DISCREPAR. La respuesta
   * se marca en local en cuanto se toca, vaya la red como vaya; si el PUT falla,
   * lo que se ve y lo que hay guardado dejan de ser lo mismo, y sin esta lista
   * nada volvería a mirarlas: el contador diría «75/75» y «Terminar» rebotaría
   * por respuestas que la persona ve marcadas en su pantalla.
   */
  const [sinGuardar, setSinGuardar] = useState<ReadonlySet<string>>(() => new Set<string>());

  /** Temporizadores de guardado, uno por pregunta que se esté escribiendo. */
  const temporizadores = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  /**
   * La escritura en curso de cada pregunta, para poder encadenar la siguiente.
   *
   * DOS TOQUES SEGUIDOS EN LA MISMA PREGUNTA SALÍAN COMO DOS PUT A LA VEZ, y el
   * servidor hace varias consultas antes de escribir: los dos manejadores se
   * entrelazan y guarda el que llegue último al UPDATE, no el que la persona
   * tocó último. Corregir un 4 por un 5 podía dejar el 4 en la base mientras la
   * pantalla enseña el 5, y eso es un dato psicométrico falso guardado en
   * silencio.
   */
  const colasDeEscritura = useRef(new Map<string, Promise<void>>());

  /**
   * A qué espera de cierre pertenece lo que llegue.
   *
   * ES UN NÚMERO Y NO UN BOOLEANO porque hay cuatro formas de que lo que se está
   * esperando deje de valer, y todas terminan igual: subiendo este contador. Se
   * agotó el plazo, la persona pulsó «Continuar», empezó otra espera, o la
   * pantalla se desmontó. Lo que llegue con un turno viejo se descarta sin tocar
   * nada — ni pintar un texto de la parte anterior sobre la siguiente, ni
   * escribir estado de un componente que ya no existe.
   */
  const turnoDelCierre = useRef(0);

  /** El plazo de la espera en curso, para poder soltarlo al desmontar. */
  const topeDelCierre = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tituloRef = useRef<HTMLHeadingElement>(null);
  const grupoAnterior = useRef(grupoActual);

  useEffect(() => {
    let vivo = true;

    void (async () => {
      const token = tokenDeLaDireccion();

      try {
        if (token) {
          try {
            await canjear(token);
          } catch (error) {
            /**
             * QUE EL CANJE FALLE NO SIGNIFICA QUE NO SE PUEDA SEGUIR. Volver a
             * tocar el mismo enlace en el chat es lo más natural del mundo para
             * retomar, y ese enlace ya está gastado — pero la cookie de la
             * primera vez sigue viva durante un día. Antes esto pintaba «este
             * enlace ya no sirve» sin llegar a preguntarle al servidor, y dejaba
             * a alguien con veinte ítems respondidos creyendo que los perdió.
             *
             * Un fallo de red sí se propaga: ahí la pantalla dice «sin conexión»
             * y recargar tiene que volver a intentarlo, así que el token no se
             * puede borrar de la dirección todavía.
             */
            if (!(error instanceof ErrorDeApi) || error.motivo === 'red') throw error;
          }

          /**
           * Fuera de la barra de direcciones en cuanto se gasta. Aunque el token
           * ya no sirva para nada, una dirección con un secreto dentro es algo
           * que la gente copia y pega sin pensarlo.
           */
          window.history.replaceState(null, '', '/');
        }

        /**
         * El perfil primero, y SU FALLO NO BLOQUEA EL CUESTIONARIO: si no se
         * puede saber si hay que preguntarlo, se pasa de largo. Preferir lo
         * contrario dejaría a alguien sin poder responder su diagnóstico por
         * una pantalla que es un complemento.
         *
         * Cuando el fallo es de sesión o de red, `leerEstado` de la línea
         * siguiente falla igual y es esa la que pinta la pantalla correcta.
         */
        let porPreguntar: TextosDelPerfil | null = null;
        try {
          const perfil = await leerPerfil();
          if (perfil.pedir) porPreguntar = perfil.textos;
        } catch {
          /* Se sigue sin preguntar. */
        }

        if (porPreguntar) {
          if (vivo) setEstado({ fase: 'perfil', textos: porPreguntar });
          return;
        }

        const datos = await leerEstado();
        if (vivo) setEstado({ fase: 'respondiendo', datos });
      } catch (error) {
        if (!vivo) return;
        setEstado({
          fase: 'fallo',
          motivo: error instanceof ErrorDeApi ? error.motivo : 'otro',
        });
      }
    })();

    return () => {
      vivo = false;
    };
  }, []);

  // Al desmontar, no dejar temporizadores sueltos apuntando a un componente que
  // ya no existe.
  useEffect(() => {
    const pendientes = temporizadores.current;
    const turno = turnoDelCierre;
    const tope = topeDelCierre;
    return () => {
      for (const temporizador of pendientes.values()) clearTimeout(temporizador);
      pendientes.clear();

      // Y la espera del cierre: subir el turno hace que la respuesta que venga
      // en camino no intente pintarse en una pantalla que ya se fue.
      if (tope.current) clearTimeout(tope.current);
      turno.current += 1;
    };
  }, []);

  /**
   * Si la página está desplazada, para despegar la cabecera del contenido. Una
   * línea y una sombra arriba del todo, sobre nada, se leen como un defecto.
   */
  useEffect(() => {
    const alDesplazar = (): void => {
      setDesplazado(window.scrollY > 4);
    };

    window.addEventListener('scroll', alDesplazar, { passive: true });
    alDesplazar();
    return () => {
      window.removeEventListener('scroll', alDesplazar);
    };
  }, []);

  /**
   * Al cambiar de parte, el foco va al título nuevo.
   *
   * Sin esto, quien navega con teclado o con lector de pantalla se queda en el
   * botón «Siguiente» mientras la pantalla entera cambió detrás: oye el mismo
   * botón otra vez y no hay nada que le diga que ya está en otra parte.
   */
  useEffect(() => {
    if (grupoAnterior.current === grupoActual) return;
    grupoAnterior.current = grupoActual;
    tituloRef.current?.focus({ preventScroll: true });
  }, [grupoActual]);

  /** Retira la pantalla de cierre cuando su animación ya terminó. */
  useEffect(() => {
    if (!celebrando) return;
    const temporizador = setTimeout(() => {
      setCelebrando(false);
    }, DURACION_CELEBRACION_MS);

    return () => {
      clearTimeout(temporizador);
    };
  }, [celebrando]);

  /** Tras el desvanecimiento, pasa a la siguiente parte. */
  useEffect(() => {
    if (!feedbackSaliendo || !pasoDeGrupo) return;
    const siguiente = pasoDeGrupo.siguiente;
    const temporizador = setTimeout(() => {
      setPasoDeGrupo(null);
      setFeedbackSaliendo(false);
      setResaltarPendientes(false);
      setGrupoActual(siguiente);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, SALIDA_FEEDBACK_MS);

    return () => {
      clearTimeout(temporizador);
    };
  }, [feedbackSaliendo, pasoDeGrupo]);

  /**
   * Una pantalla de paso que se quedó sin nada que leer no se enseña: se salta.
   *
   * PASA CUANDO SE ESPERÓ UN CIERRE QUE NO LLEGÓ y la parte siguiente tampoco
   * traía nota de entrada. Sin esto, la capa se iría con su desvanecido de medio
   * segundo, y ese medio segundo es una pantalla en blanco con un botón
   * «Continuar» en mitad de un cuestionario: se lee como que algo se rompió.
   *
   * VA COMO EFECTO Y NO DENTRO DE `soltarLaEsperaDelCierre` porque este mismo
   * caso llega por dos caminos —la respuesta vacía y el plazo agotado— y ninguno
   * de los dos tiene delante el estado con el que decidirlo.
   */
  useEffect(() => {
    if (!pasoDeGrupo || pasoDeGrupo.cierrePorLlegar) return;
    if (pasoDeGrupo.cierre !== null || pasoDeGrupo.antesala !== null) return;

    const siguiente = pasoDeGrupo.siguiente;
    setPasoDeGrupo(null);
    setFeedbackSaliendo(false);
    setResaltarPendientes(false);
    setGrupoActual(siguiente);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pasoDeGrupo]);

  /**
   * Deja de esperar el cierre de la parte y suelta la pantalla con lo que haya.
   *
   * `texto` en `null` es «se sigue sin cierre», y es una salida normal: puede que
   * esa parte no esté configurada para cerrarse con un texto, que esta vez no
   * saliera, o que se acabara el plazo. En los tres casos la persona
   * lee lo que abre la parte siguiente, si lo hay, y si no la pantalla se va
   * sola. Nadie ve un aviso de que algo falló, porque desde donde ella está no
   * ha fallado nada.
   */
  const soltarLaEsperaDelCierre = useCallback((turno: number, texto: string | null): void => {
    if (turnoDelCierre.current !== turno) return;
    // El turno sube aquí también: si el plazo se cumplió primero, lo que llegue
    // después ya no entra; y si llegó el texto, el plazo que quede no lo borra.
    turnoDelCierre.current += 1;

    setPasoDeGrupo((previo) =>
      previo?.cierrePorLlegar === true
        ? { ...previo, cierre: texto, cierrePorLlegar: false }
        : previo,
    );
  }, []);

  /**
   * Pide el cierre de una parte. Nunca lanza: `null` es «se sigue sin cierre».
   *
   * NINGÚN FALLO SALE DE AQUÍ A LA PANTALLA DE FALLO, y el de sesión caducada
   * tampoco: lo que estaba respondiendo ya está guardado, y el resto del flujo
   * —guardar una respuesta, cerrar el cuestionario— detecta la sesión por su
   * cuenta y con mucho mejor sitio para decirlo. Sacar a alguien de aquí sería
   * cambiarle la pantalla por un texto de adorno que no llegó.
   */
  const pedirElCierre = useCallback(async (grupoId: string): Promise<string | null> => {
    try {
      return await cierreDeParte(grupoId);
    } catch {
      return null;
    }
  }, []);

  /**
   * Se queda pendiente de una petición que ya tardó más de la cuenta, con la
   * pantalla de espera abierta, y la suelta cuando llegue o cuando se agote el
   * plazo.
   *
   * EL PLAZO SE GUARDA EN UNA VARIABLE PROPIA además de en la referencia: una
   * petición muy lenta puede volver cuando la persona ya está en otra parte
   * esperando otro cierre distinto, y mirar solo la referencia haría que la
   * vieja le quitara el plazo a la nueva, que se quedaría esperando sin techo.
   */
  const vigilarLaEspera = useCallback(
    (turno: number, peticion: Promise<string | null>): void => {
      if (topeDelCierre.current) clearTimeout(topeDelCierre.current);
      const plazo = setTimeout(() => {
        soltarLaEsperaDelCierre(turno, null);
      }, TOPE_DE_ESPERA_DEL_CIERRE_MS);
      topeDelCierre.current = plazo;

      void peticion.then((texto) => {
        clearTimeout(plazo);
        if (topeDelCierre.current === plazo) topeDelCierre.current = null;
        soltarLaEsperaDelCierre(turno, texto);
      });
    },
    [soltarLaEsperaDelCierre],
  );

  /** Escribe una respuesta y anota si no llegó. Nunca lanza. */
  const escribirEnServidor = useCallback(
    async (preguntaId: string, valor: ValorDeRespuesta): Promise<void> => {
      try {
        await guardar(preguntaId, valor);
        setSinGuardar((previas) => {
          if (!previas.has(preguntaId)) return previas;
          const siguientes = new Set(previas);
          siguientes.delete(preguntaId);
          return siguientes;
        });
      } catch (error) {
        if (error instanceof ErrorDeApi && error.motivo === 'sin_sesion') {
          setEstado({ fase: 'fallo', motivo: 'sin_sesion' });
          return;
        }
        /**
         * Se anota en vez de reintentar en silencio. Quien está respondiendo
         * tiene que enterarse de que sus respuestas no están llegando: seguir
         * contestando media hora contra un servidor caído, creyendo que se
         * guarda, es el peor resultado posible.
         */
        setSinGuardar((previas) => new Set(previas).add(preguntaId));
      }
    },
    [],
  );

  /** Pone una escritura detrás de la anterior de esa misma pregunta. */
  const encolarEscritura = useCallback(
    (preguntaId: string, valor: ValorDeRespuesta): Promise<void> => {
      const anterior = colasDeEscritura.current.get(preguntaId) ?? Promise.resolve();
      // `escribirEnServidor` no lanza nunca, así que la cadena no se puede
      // romper y dejar una pregunta sin poder volver a guardarse.
      const siguiente = anterior.then(() => escribirEnServidor(preguntaId, valor));
      colasDeEscritura.current.set(preguntaId, siguiente);
      return siguiente;
    },
    [escribirEnServidor],
  );

  const alResponder = useCallback(
    (pregunta: Pregunta, valor: ValorDeRespuesta): void => {
      // Primero en pantalla: la respuesta se ve marcada al instante, vaya la red
      // como vaya.
      setEstado((previo) => {
        if (previo.fase !== 'respondiendo') return previo;
        const otras = previo.datos.respuestas.filter((r) => r.preguntaId !== pregunta.id);
        return {
          fase: 'respondiendo',
          datos: {
            ...previo.datos,
            respuestas: [...otras, { preguntaId: pregunta.id, valor }],
          },
        };
      });

      const anterior = temporizadores.current.get(pregunta.id);
      if (anterior) clearTimeout(anterior);

      /**
       * Escribir espera; pulsar no. Un texto largo dispararía una petición por
       * tecla, mientras que una escala o una opción son una decisión tomada y
       * guardarla al momento es lo que hace que cerrar el navegador entre dos
       * preguntas no pierda nada.
       */
      if (pregunta.tipo === 'texto') {
        temporizadores.current.set(
          pregunta.id,
          setTimeout(() => {
            temporizadores.current.delete(pregunta.id);
            void encolarEscritura(pregunta.id, valor);
          }, ESPERA_DE_ESCRITURA_MS),
        );
        return;
      }

      void encolarEscritura(pregunta.id, valor);
    },
    [encolarEscritura],
  );

  /**
   * Manda todo lo que el servidor todavía no tiene: lo que estaba esperando su
   * turno para guardarse y lo que en su día no llegó.
   */
  const ponerseAlDia = useCallback(async (): Promise<void> => {
    const enEspera = [...temporizadores.current.keys()];
    for (const preguntaId of enEspera) {
      const temporizador = temporizadores.current.get(preguntaId);
      if (temporizador) clearTimeout(temporizador);
      temporizadores.current.delete(preguntaId);
    }

    if (estado.fase !== 'respondiendo') return;

    const porMandar = new Set([...enEspera, ...sinGuardar]);
    await Promise.all(
      [...porMandar].map((preguntaId) => {
        const respuesta = estado.datos.respuestas.find((r) => r.preguntaId === preguntaId);
        return respuesta ? encolarEscritura(preguntaId, respuesta.valor) : Promise.resolve();
      }),
    );
  }, [estado, sinGuardar, encolarEscritura]);

  /**
   * Guarda el perfil y entra al cuestionario.
   *
   * SI SE GUARDÓ Y LUEGO FALLA LEER EL CUESTIONARIO, lo guardado sigue guardado:
   * al volver a abrir el enlace ya no se le pregunta otra vez. Por eso los dos
   * pasos se tratan aparte y el segundo lleva a la pantalla de fallo normal.
   */
  const alGuardarPerfil = useCallback((datos: DatosDelPerfil): void => {
    setGuardandoPerfil(true);
    setFalloDelPerfil(null);

    void (async () => {
      try {
        await guardarPerfil(datos);
      } catch (error) {
        setGuardandoPerfil(false);
        setFalloDelPerfil(
          error instanceof ErrorDeApi && error.motivo === 'red'
            ? 'No se pudo guardar: revisa la conexión y vuelve a intentarlo.'
            : 'No se pudo guardar. Vuelve a intentarlo.',
        );
        return;
      }

      try {
        setEstado({ fase: 'respondiendo', datos: await leerEstado() });
      } catch (error) {
        setEstado({
          fase: 'fallo',
          motivo: error instanceof ErrorDeApi ? error.motivo : 'otro',
        });
      }
    })();
  }, []);

  if (estado.fase === 'cargando') {
    return (
      <Pantalla>
        <img
          className="pantalla__marca pantalla__marca--latiendo"
          src="/marca/logo-sobre-claro.svg"
          alt="Ser Prana"
        />
        <p>Un momento…</p>
      </Pantalla>
    );
  }

  if (estado.fase === 'perfil') {
    return (
      <Perfil
        textos={estado.textos}
        enviando={guardandoPerfil}
        fallo={falloDelPerfil}
        alGuardar={alGuardarPerfil}
      />
    );
  }

  if (estado.fase === 'fallo') {
    return (
      <Pantalla>
        <div className="pantalla__tarjeta">{mensajeDeFallo(estado.motivo)}</div>
      </Pantalla>
    );
  }

  if (estado.fase === 'completado') {
    /**
     * Lo que había hasta ahora: la palomita, el texto de cierre y «te
     * escribimos por WhatsApp». Sigue siendo lo que se ve mientras no haya mapa
     * —falta que Ángela apruebe el catálogo y escriba el encuadre—, y también
     * si el mapa no se pudo pedir. No es una pantalla de repuesto: es un final
     * digno por sí mismo.
     */
    const cierre = (
      <Pantalla>
        <div className="pantalla__tarjeta">
          <img className="pantalla__marca" src="/marca/logo-sobre-claro.svg" alt="Ser Prana" />
          <h1>Listo</h1>
          {/*
           * El texto de cierre se repite aquí abajo a propósito. Arriba pasa
           * en unos segundos, y quien lee despacio —o lo mira justo cuando se
           * está yendo— tiene que poder volver a encontrarlo.
           */}
          {estado.mensaje !== null && <p className="pantalla__cierre">{estado.mensaje}</p>}
          <p>Te escribimos por WhatsApp para seguir desde ahí; puedes cerrar esta página.</p>
        </div>
      </Pantalla>
    );

    return (
      <>
        {/*
         * El mapa se pide mientras la celebración todavía está en pantalla, así
         * que para cuando la animación se va ya hay algo debajo. Al revés
         * —esperar a que termine para empezar a pedirlo— dejaría tres segundos
         * de página en blanco justo en el momento de más atención.
         */}
        <Mapa respaldo={cierre} />

        {celebrando && <Celebracion mensaje={estado.mensaje} />}
      </>
    );
  }

  if (estado.fase === 'resultadosConocidos') {
    return (
      <ResultadosConocidos
        cuestionario={estado.datos.cuestionario}
        alTerminar={(mensaje) => {
          setEstado({ fase: 'completado', mensaje });
          setCelebrando(true);
        }}
        alVolver={() => {
          setEstado({ fase: 'respondiendo', datos: estado.datos });
          window.scrollTo({ top: 0, behavior: 'instant' });
        }}
        alPerderSesion={() => {
          setEstado({ fase: 'fallo', motivo: 'sin_sesion' });
        }}
      />
    );
  }

  const { cuestionario, respuestas } = estado.datos;
  const grupo = cuestionario.grupos[grupoActual];
  if (!grupo) {
    return (
      <Pantalla>
        <div className="pantalla__tarjeta">{mensajeDeFallo('otro')}</div>
      </Pantalla>
    );
  }

  const valorDe = (preguntaId: string): ValorDeRespuesta | undefined =>
    respuestas.find((r) => r.preguntaId === preguntaId)?.valor;

  /**
   * Las que faltan de esta parte. NO INCLUYE las que no se pudieron guardar: la
   * persona ya las contestó, y bloquearle el paso por una mala conexión la
   * dejaría atascada en una pantalla sin nada que pueda hacer. Se reintentan al
   * avanzar y, si aun así no llegan, el servidor lo dirá al cerrar.
   */
  const faltanDelGrupo = grupo.preguntas.filter(
    (pregunta) => pregunta.obligatoria && valorDe(pregunta.id) === undefined,
  );

  const esElUltimo = grupoActual === cuestionario.grupos.length - 1;
  const progreso = contarProgreso(cuestionario, respuestas, sinGuardar);

  const irAGrupo = (indice: number): void => {
    setResaltarPendientes(false);
    setGrupoActual(indice);
    /**
     * Instantáneo y no suave, aunque el resto de la página sí lo sea. El
     * contenido ya cambió: ver quince frases nuevas pasar de largo mientras la
     * página sube dos pantallas marea más que ayudar.
     */
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  /**
   * Qué hacer cuando «Terminar» no salió.
   *
   * El caso que importa es `faltan_respuestas`: el servidor no tiene todo, y él
   * sabe exactamente qué le falta. Se recarga lo suyo —que manda sobre lo que
   * hubiera en pantalla— y se lleva a la persona a la parte donde está la
   * primera que falta. Antes esto era un callejón sin salida: el botón seguía
   * ahí, volver a pulsarlo daba lo mismo, y lo que faltaba se veía respondido.
   */
  const recuperarDelCierre = async (error: unknown): Promise<void> => {
    if (error instanceof ErrorDeApi && error.motivo === 'sin_sesion') {
      setEstado({ fase: 'fallo', motivo: 'sin_sesion' });
      return;
    }

    if (!(error instanceof ErrorDeApi) || error.motivo !== 'faltan_respuestas') {
      setProblemaAlTerminar('no_salio');
      return;
    }

    try {
      const datos = await leerEstado();
      setEstado({ fase: 'respondiendo', datos });
      setSinGuardar(new Set());
      setGrupoActual(primerGrupoConAlgunaDe(datos.cuestionario, datos.progreso.faltan));
      setResaltarPendientes(true);
      setProblemaAlTerminar('faltan');
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch {
      setProblemaAlTerminar('no_salio');
    }
  };

  const avanzar = async (): Promise<void> => {
    if (faltanDelGrupo.length > 0) {
      setResaltarPendientes(true);
      // A la primera que falta, y no al principio del grupo: con cinco frases
      // largas, «falta una» sin decir cuál obliga a releerlas todas.
      const primera = faltanDelGrupo[0];
      if (primera) {
        document.getElementById(anclaDe(primera.id))?.scrollIntoView({ block: 'center' });
      }
      return;
    }

    setResaltarPendientes(false);
    setProblemaAlTerminar(null);

    /**
     * EL BOTÓN SE BLOQUEA DESDE AQUÍ Y NO DESDE MÁS ABAJO, y no es para evitar
     * un doble envío de las respuestas: es que lo que viene puede tardar —una
     * respuesta que aún no había llegado al servidor, y después el cierre de la
     * parte—, y sin bloquearlo, un segundo toque en esa ventana pide otro cierre
     * para la misma parte. Eso son dos textos escritos y pagados de los que solo
     * se lee uno. Mientras tanto el botón dice «Guardando…», así que el toque sí
     * tiene respuesta.
     */
    setEnviando(true);
    try {
      await ponerseAlDia();

      if (!esElUltimo) {
        const siguiente = grupoActual + 1;
        const queSigue = cuestionario.grupos[siguiente];
        const paso = queSigue ? pantallaDePaso(grupo, queSigue, respuestas) : null;

        if (!paso) {
          irAGrupo(siguiente);
          return;
        }

        const abrirLaPantalla = (conCierre: PantallaDePaso): void => {
          setPasoDeGrupo({ ...conCierre, siguiente });
          setFeedbackSaliendo(false);
          window.scrollTo({ top: 0, behavior: 'instant' });
        };

        if (!paso.cierrePorLlegar) {
          abrirLaPantalla(paso);
          return;
        }

        /**
         * SE LE DA UN MOMENTO ANTES DE ABRIR NADA. Si el cierre llega dentro del
         * umbral —y hoy llega siempre, porque las quince partes vienen de
         * fábrica sin cierre escrito y el servidor contesta «no hay» enseguida—
         * la pantalla se abre ya con lo
         * que haya que leer, o no se abre en absoluto. Abrirla antes de saberlo
         * dejaría un parpadeo entre cada parte.
         */
        const turno = (turnoDelCierre.current += 1);
        const peticion = pedirElCierre(grupo.id);
        const aTiempo = await Promise.race([peticion, alCabo(UMBRAL_ANTES_DE_ESPERAR_MS, TARDA)]);

        // La pantalla se fue mientras se esperaba: nada que abrir ni que vigilar.
        if (turnoDelCierre.current !== turno) return;

        if (aTiempo === TARDA) {
          abrirLaPantalla(paso);
          vigilarLaEspera(turno, peticion);
          return;
        }

        turnoDelCierre.current += 1;
        if (aTiempo === null && paso.antesala === null) {
          // No hay nada que leer y no lo va a haber: se pasa de parte sin más.
          irAGrupo(siguiente);
          return;
        }
        abrirLaPantalla({ ...paso, cierre: aTiempo, cierrePorLlegar: false });
        return;
      }

      /**
       * LA ÚLTIMA PARTE NO PIDE CIERRE, Y ES A PROPÓSITO. Aquí no hay pantalla
       * de paso —no hay una parte siguiente que abrir—: se pulsa «Terminar» y lo
       * que viene es el cierre del cuestionario entero, que es otro texto y sale
       * de otro sitio. Un cierre de parte aquí no tendría dónde leerse, y el
       * panel avisa de eso al configurarlo.
       */
      try {
        const { mensaje } = await completar();
        setEstado({ fase: 'completado', mensaje });
        setCelebrando(true);
        window.scrollTo({ top: 0, behavior: 'instant' });
      } catch (error) {
        await recuperarDelCierre(error);
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Cabecera
        parte={grupoActual + 1}
        partes={cuestionario.grupos.length}
        hechas={progreso.hechas}
        total={progreso.total}
        desplazado={desplazado}
      />

      <main className="hoja">
        {/*
         * La marca solo en la primera parte. No va en la cabecera fija porque el
         * logotipo es de trazo muy fino y al tamaño que cabe ahí no se lee; aquí
         * aparece al abrir la página, que es cuando alguien decide si se fía de
         * un formulario que le llegó por un chat.
         */}
        {grupoActual === 0 && (
          <img className="marca" src="/marca/logo-sobre-claro.svg" alt="Ser Prana" />
        )}

        {grupoActual === 0 && cuestionario.introduccion && (
          <p className="introduccion">{cuestionario.introduccion}</p>
        )}

        {/*
         * El atajo para quien ya tiene sus resultados. Solo en la primera parte:
         * más adelante, quien ya lleva respondida media hora no viene a saltarse
         * nada, y ofrecérselo ahí sería invitarle a tirar lo que lleva hecho.
         *
         * Solo si el servidor dice que se puede con este cuestionario. Ver
         * `admiteResultadosConocidos`.
         */}
        {grupoActual === 0 && estado.datos.admiteResultadosConocidos === true && (
          <button
            type="button"
            className="atajo"
            onClick={() => {
              setEstado({ fase: 'resultadosConocidos', datos: estado.datos });
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
          >
            Ya sé mis resultados
          </button>
        )}

        {/*
         * La `key` remonta el bloque entero al cambiar de parte, y es lo que
         * hace que las tarjetas vuelvan a entrar escalonadas. Sin ella React
         * reutilizaría los mismos nodos cambiándoles el texto, y quince partes
         * seguidas parecerían la misma pantalla con otras frases.
         */}
        <section className="grupo" key={grupo.id}>
          <h1 className="grupo__titulo" ref={tituloRef} tabIndex={-1}>
            {tituloDeGrupo(grupo)}
          </h1>
          {grupo.descripcion && <p className="grupo__descripcion">{grupo.descripcion}</p>}

          <div className="preguntas">
            {grupo.preguntas.map((pregunta) => (
              <PreguntaEnPantalla
                key={pregunta.id}
                id={anclaDe(pregunta.id)}
                pregunta={pregunta}
                valor={valorDe(pregunta.id)}
                resaltarPendiente={resaltarPendientes}
                sinGuardar={sinGuardar.has(pregunta.id)}
                alResponder={(valor) => {
                  alResponder(pregunta, valor);
                }}
              />
            ))}
          </div>
        </section>

        {sinGuardar.size > 0 && (
          <p className="aviso aviso--problema" role="alert">
            {sinGuardar.size === 1
              ? 'Una de tus respuestas no ha llegado a guardarse. Revisa la conexión: se vuelve a intentar al pasar de parte.'
              : `${String(sinGuardar.size)} de tus respuestas no han llegado a guardarse. Revisa la conexión: se vuelven a intentar al pasar de parte.`}
          </p>
        )}

        {problemaAlTerminar === 'faltan' && (
          <p className="aviso aviso--problema" role="alert">
            Algunas respuestas no llegaron a guardarse. Te dejamos en la parte donde falta
            contestar.
          </p>
        )}

        {problemaAlTerminar === 'no_salio' && (
          <p className="aviso aviso--problema" role="alert">
            No se pudo terminar. Revisa la conexión y vuelve a pulsar «Terminar».
          </p>
        )}

        {resaltarPendientes && faltanDelGrupo.length > 0 && (
          <p className="aviso aviso--problema" role="alert">
            {faltanDelGrupo.length === 1
              ? 'Queda una pregunta por responder en esta pantalla.'
              : `Quedan ${String(faltanDelGrupo.length)} preguntas por responder en esta pantalla.`}
          </p>
        )}

        <div className="botonera">
          {grupoActual > 0 && (
            <button
              type="button"
              className="boton boton--secundario"
              onClick={() => {
                irAGrupo(grupoActual - 1);
              }}
            >
              Atrás
            </button>
          )}

          <button
            type="button"
            className="boton"
            disabled={enviando || pasoDeGrupo !== null}
            onClick={() => {
              void avanzar();
            }}
          >
            {enviando ? 'Guardando…' : esElUltimo ? 'Terminar' : 'Siguiente'}
          </button>
        </div>
      </main>

      {pasoDeGrupo && (
        <PasoDeGrupo
          paso={pasoDeGrupo}
          saliendo={feedbackSaliendo}
          alContinuar={() => {
            if (feedbackSaliendo) return;
            /**
             * Quien pulsa «Continuar» deja de esperar, y lo que llegue después no
             * puede aparecer: la pantalla ya se está apagando y el texto saldría
             * a medio desvanecer, o peor, encima de la parte siguiente.
             */
            turnoDelCierre.current += 1;
            setFeedbackSaliendo(true);
          }}
        />
      )}
    </>
  );
}

/** Una pantalla con una sola cosa dentro: cargando, un fallo, o el final. */
function Pantalla({ children }: { children: React.ReactNode }) {
  return <main className="pantalla">{children}</main>;
}

/**
 * La cabecera fija: cuánto se lleva, siempre a la vista.
 *
 * SE QUEDA ARRIBA AL DESPLAZARSE porque es lo único que sostiene a alguien en el
 * ítem 40 de 75. Si el progreso se va con el scroll, lo que queda en pantalla es
 * una lista sin fin de frases sobre uno mismo.
 *
 * SIN LA MARCA DENTRO. El logotipo es de trazo muy fino y al alto que cabe en
 * una cabecera se ve como una mancha: aparece arriba de la primera parte, con
 * sitio para leerse. Aquí solo va lo que hace falta mirar quince veces.
 *
 * LA BARRA MIDE PREGUNTAS Y NO PARTES, aunque el texto hable de partes. Con
 * quince partes, la barra se movería una vez cada cinco respuestas y parecería
 * congelada; con setenta y cinco preguntas avanza en cada toque, que es el
 * único momento en que alguien mira si está llegando.
 *
 * Es el `<progress>` nativo y no un div con un ancho calculado, por una razón
 * que muerde en producción y no en desarrollo: la CSP del servidor es
 * `style-src 'self'` sin `unsafe-inline`, así que un `style={{width}}` se
 * aplicaría con Vite delante y lo bloquearía el navegador una vez desplegado.
 * De paso, el elemento nativo ya se anuncia solo a un lector de pantalla.
 */
function Cabecera({
  parte,
  partes,
  hechas,
  total,
  desplazado,
}: {
  parte: number;
  partes: number;
  hechas: number;
  total: number;
  desplazado: boolean;
}) {
  return (
    <header className={`cabecera${desplazado ? ' cabecera--desplazada' : ''}`}>
      <progress
        className="cabecera__barra"
        value={hechas}
        // Un cuestionario sin ninguna pregunta obligatoria dejaría `max` en cero,
        // que no es un valor válido y pinta la barra llena.
        max={Math.max(total, 1)}
        aria-label="Progreso del cuestionario"
      />

      <div className="cabecera__cuerpo">
        <p className="cabecera__parte">
          Parte {parte} de {partes}
        </p>
        <p className="cabecera__cuenta">{total === 0 ? 0 : Math.round((hechas / total) * 100)}%</p>
      </div>
    </header>
  );
}

/**
 * La pantalla de cierre, a pantalla completa y durante unos segundos.
 *
 * EL TEXTO NO SE ESCRIBE AQUÍ. Viene de `mensajes_diagnostico`, que redacta la
 * psicóloga desde el panel. Cuando no se puede leer queda solo la palomita: es
 * preferible a que la interfaz se invente un cierre
 * para alguien que acaba de contar setenta y cinco cosas sobre sí mismo, porque
 * quien lo lea no va a distinguir esa frase de una escrita por su terapeuta.
 *
 * Se va sola, y debajo está lo mismo: el mensaje no se pierde por que la
 * animación se haya acabado antes de terminar de leerlo.
 */
function Celebracion({ mensaje }: { mensaje: string | null }) {
  return (
    <div className="celebracion" role="status" aria-live="polite">
      <svg className="celebracion__palomita" viewBox="0 0 52 52" aria-hidden="true">
        <circle className="celebracion__circulo" cx="26" cy="26" r="23" />
        <path className="celebracion__trazo" d="M15 26.5 L22.5 34 L37 18" />
      </svg>

      {mensaje !== null && <p className="celebracion__texto">{mensaje}</p>}
    </div>
  );
}

/**
 * El umbral entre dos partes del cuestionario.
 *
 * DOS TEXTOS, LOS DOS DE LA PSICÓLOGA: arriba lo que cierra la parte recién
 * respondida y abajo lo que abre la que viene. Aquí no se inventa ninguno de los
 * dos, y cualquiera puede faltar.
 *
 * LA LÍNEA DEL MEDIO NO ES ADORNO. Sin ella, dos párrafos seguidos se leen como
 * uno solo y la frase que abre la parte siguiente parece la última del bloque
 * anterior, que es justo lo contrario de lo que se busca. Solo aparece cuando de
 * verdad hay dos textos.
 *
 * Se sostiene unos segundos —los que diga `duracionDelPaso`, según lo que haya
 * que leer— y «Continuar» lo salta.
 *
 * MIENTRAS EL CIERRE VIENE EN CAMINO NO SE ENSEÑA NADA MÁS QUE LA ESPERA, ni
 * siquiera lo que abre la parte siguiente. Los dos textos entran coreografiados
 * —primero el que cierra, luego la línea, luego el que abre—, y sacar el segundo
 * antes que el primero para luego meterle el otro por encima daría un salto en
 * mitad de la lectura. Lo que se ve entretanto no dice qué se está haciendo por
 * dentro: eso no es asunto de quien está respondiendo.
 */
function PasoDeGrupo({
  paso,
  saliendo,
  alContinuar,
}: {
  paso: PantallaDePaso;
  saliendo: boolean;
  alContinuar: () => void;
}) {
  // Ref para no reiniciar la cuenta cada vez que el padre se vuelve a pintar.
  const alContinuarRef = useRef(alContinuar);
  alContinuarRef.current = alContinuar;

  const ritmo = ritmoDelPaso(paso);
  const duracion = duracionDelPaso(paso);

  /**
   * El temporizador que retira la pantalla sola.
   *
   * CON `duracion` EN `null` NO SE ARRANCA, que es el caso de estar esperando el
   * cierre: un temporizador corriendo ahí se llevaría la pantalla justo antes de
   * que llegue el texto. Cuando llega —o cuando se desiste— `duracion` deja de
   * ser `null`, este efecto se vuelve a montar y la cuenta empieza entonces, ya
   * medida sobre lo que de verdad hay que leer.
   */
  useEffect(() => {
    if (saliendo || duracion === null) return;
    const temporizador = setTimeout(() => {
      alContinuarRef.current();
    }, duracion);
    return () => {
      clearTimeout(temporizador);
    };
  }, [saliendo, duracion]);

  return (
    <div
      className={`feedback feedback--${ritmo}` + (saliendo ? ' feedback--saliendo' : '')}
      role="dialog"
      aria-modal="true"
      aria-live="polite"
    >
      {paso.cierrePorLlegar ? (
        <p className="feedback__espera">Un momento…</p>
      ) : (
        <>
          {paso.cierre !== null && <p className="feedback__texto">{paso.cierre}</p>}

          {paso.cierre !== null && paso.antesala !== null && (
            <span className="feedback__hilo" aria-hidden="true" />
          )}

          {paso.antesala !== null && (
            <p className="feedback__texto feedback__texto--antesala">{paso.antesala}</p>
          )}
        </>
      )}

      <div className="feedback__pie">
        {/*
         * La barra solo cuando ya hay algo que cronometrar. Su vaciado es una
         * animación de CSS que arranca al montarse: si estuviera puesta durante
         * la espera, llegaría vacía al momento en que empieza a contarse de
         * verdad y diría que no queda tiempo justo cuando aparece el texto.
         */}
        {!paso.cierrePorLlegar && (
          <div
            className="feedback__barra"
            role="progressbar"
            aria-label="Tiempo restante"
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="feedback__barra-relleno" />
          </div>
        )}

        {/*
         * «Continuar» SIEMPRE, también mientras se espera. Nadie tiene por qué
         * quedarse retenido en una pantalla por un texto que quizá no llegue.
         */}
        <button
          type="button"
          className="feedback__continuar"
          onClick={alContinuar}
          disabled={saliendo}
        >
          Continuar
        </button>
      </div>
    </div>
  );
}

/**
 * Qué se le dice a la persona cuando algo no se pudo hacer.
 *
 * Cada motivo lleva a una salida distinta, y ninguno dice «error»: quien está
 * delante no puede hacer nada con esa palabra. Lo que necesita saber es si tiene
 * que hacer algo, esperar, o si no depende de ella.
 *
 * `faltan_respuestas` no sale por aquí: ese se resuelve dentro del cuestionario,
 * volviendo a la pregunta que falta, y sacar a alguien a una pantalla de fallo
 * por eso sería perder el sitio donde ya estaba.
 */
function mensajeDeFallo(motivo: MotivoDeFallo): React.ReactNode {
  if (motivo === 'sin_sesion' || motivo === 'enlace_invalido') {
    return (
      <>
        <h1>Este enlace ya no sirve</h1>
        <p>
          Los enlaces se usan una sola vez y caducan. Escríbenos por WhatsApp y te mandamos uno
          nuevo: lo que hayas respondido sigue guardado y podrás seguir donde ibas.
        </p>
      </>
    );
  }

  if (motivo === 'sin_contenido') {
    return (
      <>
        <h1>Todavía no está listo</h1>
        <p>
          El cuestionario aún no está disponible. No es nada que hayas hecho mal; te avisamos por
          WhatsApp en cuanto puedas responderlo.
        </p>
      </>
    );
  }

  if (motivo === 'red') {
    return (
      <>
        <h1>Sin conexión</h1>
        <p>No se pudo conectar. Revisa tu conexión y vuelve a cargar la página.</p>
      </>
    );
  }

  return (
    <>
      <h1>No se pudo abrir</h1>
      <p>Vuelve a intentarlo en un rato. Si sigue igual, escríbenos por WhatsApp.</p>
    </>
  );
}
